import { use } from "react";
import type { SegmentControlContext, Segments } from "./types";

export const useSegmentControlMatcher = (
	entries: Segments,
	values: SegmentControlContext["segments"],
) => {
	if (entries === undefined)
		return {
			rule: "ALLOW",
		} as const;

	const matchedEntries = Object.entries(values)
		.map(([key, value]) => {
			const possibleValues = entries[key];

			// handle boolean
			if (typeof value === "boolean" && typeof entries[key] !== "undefined") {
				return {
					match: value === entries[key],
				};
			}

			if (!possibleValues || !Array.isArray(possibleValues)) return null;

			if (!possibleValues.length) return null;

			if (isPromise(value)) {
				const resolvedValue = use(value);
				return {
					match: possibleValues.some((entry) => entry === resolvedValue),
					possible: possibleValues,
				};
			}

			return {
				match: possibleValues.some((entry) => entry === value),
				possible: possibleValues,
			};
		})
		.filter(Boolean);

	if (matchedEntries.length === 0)
		return {
			rule: "ALLOW",
		} as const;

	if (matchedEntries.some((entry) => !entry.match))
		return {
			rule: "DENY",
		} as const;

	return {
		rule: "ALLOW",
	} as const;
};

const isPromise = <T>(value: T | Promise<T>): value is Promise<T> => {
	return value && typeof (value as Promise<T>).then === "function";
};

/**
 * Plain (non-hook) version of {@link useSegmentControlMatcher} so segment
 * matching can be reused inside loops — e.g. filtering content cards before
 * positioning them in the product grid.
 *
 * Unlike the hook it does not `use()`-unwrap promise-valued segments, so it
 * assumes resolved values — always the case for the values provided in
 * `Providers.tsx` (country string + loyalty boolean).
 */
export const matchSegments = (entries: Segments, values: SegmentControlContext["segments"]) => {
	if (entries === undefined)
		return {
			rule: "ALLOW",
		} as const;

	const matchedEntries = Object.entries(values)
		.map(([key, value]) => {
			const possibleValues = entries[key];

			// handle boolean
			if (typeof value === "boolean" && typeof entries[key] !== "undefined") {
				return {
					match: value === entries[key],
				};
			}

			if (!possibleValues || !Array.isArray(possibleValues)) return null;

			if (!possibleValues.length) return null;

			return {
				match: possibleValues.some((entry) => entry === value),
				possible: possibleValues,
			};
		})
		.filter(Boolean);

	if (matchedEntries.length === 0)
		return {
			rule: "ALLOW",
		} as const;

	if (matchedEntries.some((entry) => !entry.match))
		return {
			rule: "DENY",
		} as const;

	return {
		rule: "ALLOW",
	} as const;
};
